/**
 * Point d'entree du jeu.
 *
 * Le chapitre 1 est pilote par `ChapterApp` (src/chapter.ts), qui enchaine les
 * neuf scenes narratives (voir docs/process/adr/0011-moteur-narratif-etat-de-partie-et-radio.md)
 * et traite la phase tactique finale comme une scene parmi les autres.
 */

import './ui/theme.css';
import './ui/styles.css';
import { ChapterApp } from './chapter';
import { installDebugApi } from './debug/gameApi';
import { TitleView } from './ui/titleView';
import { loadArchivedDossier, loadSession, saveDossier, saveSession } from './core/save';
import { randomSeedLabel } from './core/rng';
import { buildScenePresetStart } from './dev/scenePresets';
import type { ChapterId } from './narrative';
import { CHAPTERS } from './data/chapters';
import type { ProfileId } from './data/chapters';
import { preloadCadetAssets } from './render/exploration/characterAssets';

const container = document.getElementById('app');
if (!container) throw new Error('#app introuvable dans index.html');

// Les scènes d'exploration sont synchrones : le jeu ne construit ChapterApp qu'après les
// GLB locaux. La page ne reste jamais blanche en cas de cache vidé ou de fichier manquant.
const assetStatus = document.createElement('p');
assetStatus.className = 'panel';
assetStatus.style.cssText =
  'position:fixed;inset:40% auto auto 50%;transform:translateX(-50%);padding:1rem 1.5rem;z-index:10';
assetStatus.textContent = 'Chargement des personnages…';
container.appendChild(assetStatus);
let characterAssetsReady = true;
try {
  await preloadCadetAssets();
  assetStatus.remove();
} catch (error) {
  console.error('[HOLT] chargement des personnages impossible', error);
  assetStatus.textContent = 'Impossible de charger les personnages.';
  const retry = document.createElement('button');
  retry.type = 'button';
  retry.className = 'btn btn--primary';
  retry.textContent = 'Réessayer';
  retry.addEventListener('click', () => window.location.reload());
  assetStatus.append(' ', retry);
  characterAssetsReady = false;
}

if (characterAssetsReady) {
  /**
   * `?seed=xxx` permet de rejouer exactement une partie. `?ai=0` accelere les tests.
   * `?scene=<id>` demarre directement sur une scene precise (developpement, tests e2e) :
   * voir docs/process/DEBUG_API.md. `?chapter=N` choisit le chapitre (ADR 0021) -- sans
   * effet si `?scene=` est aussi donne, qui deduit deja son chapitre (`chapterOfScene`).
   */
  const params = new URLSearchParams(window.location.search);
  const seed = params.get('seed') ?? undefined;
  const aiDelay = params.get('ai') === '0' ? 0 : Number(params.get('ai') ?? 450);
  const startSceneId = params.get('scene') ?? undefined;
  const rawChapter = Number(params.get('chapter'));
  const chapterId: ChapterId | undefined = rawChapter === 1 || rawChapter === 2 ? rawChapter : undefined;
  /**
   * `?profile=<id>` choisit un profil de depart du chapitre 2 (ADR 0022 §6, dev/tests) -- sans
   * effet si `?chapter=2` n'est pas aussi donne. Une valeur inconnue est ignoree (repli sur le
   * profil Neutre, voir `ChapterApp.startingDossier`).
   */
  const rawProfile = params.get('profile');
  const profileId: ProfileId | undefined =
    rawProfile === 'loyal' || rawProfile === 'solitaire' || rawProfile === 'neutre' ? rawProfile : undefined;
  /** `?dice=0` desactive la mise en scene du de 3D (voir docs/process/DEBUG_API.md) : utile pour un parcours de test. */
  const diceEnabled = params.get('dice') !== '0';

  /**
   * Ecran titre (docs/art/UI-DESIGN-SYSTEM.md, "Écran titre") : saute
   * entierement des que l'URL porte `?seed=`, `?scene=` ou `?chapter=` -- ce sont
   * des outils de dev et de test (voir docs/process/DEBUG_API.md) qui doivent
   * demarrer DIRECTEMENT dans la partie, jamais derriere un ecran a cliquer
   * (les tests e2e s'appuient explicitement sur ce raccourci).
   */
  const showTitle = seed === undefined && startSceneId === undefined && chapterId === undefined;
  // Lu AVANT la construction de `ChapterApp` : son constructeur reprend
  // silencieusement une session existante des que les parametres le
  // permettent (voir `isResumingRun` dans chapter.ts). C'est donc le seul
  // moyen de savoir si CE chargement est une vraie reprise, pour proposer ou
  // non le bouton "Reprendre" -- une fois `ChapterApp` construit, l'etat
  // resume et l'etat "partie neuve faute de session" sont indiscernables.
  const hadResumableRun = showTitle && Boolean(loadSession().run);

  // `ChapterApp` (et donc `window.__game`) est construit inconditionnellement,
  // meme quand l'ecran titre va s'afficher par-dessus : la partie tourne deja
  // en arriere-plan (voir le lot "Écran titre" de la passe UI), ce qui est ce
  // qui permet a `window.__game` de fonctionner immediatement, y compris
  // pendant que le titre est a l'ecran.
  const chapter = new ChapterApp(container, {
    seed,
    chapter: chapterId,
    aiDelayMs: Number.isFinite(aiDelay) ? aiDelay : 450,
    startSceneId,
    diceEnabled,
    profile: profileId,
  });
  installDebugApi(chapter);

  // Utile en developpement comme pour les rapports de bug.
  console.info(
    `[HOLT] chapitre ${chapter.run.chapter} - graine "${chapter.run.seed}" - scene "${chapter.run.sceneId}"`,
  );

  if (showTitle) {
    chapter.setMenuVisible(true);
    const resumeSceneTitle = hadResumableRun
      ? (CHAPTERS[chapter.run.chapter].scenes.find((s) => s.id === chapter.run.sceneId)?.title ??
        chapter.run.sceneId)
      : null;
    // ADR 0022 §3 : decide si « Chapitre 2 » reprend l'archive du chapitre 1 directement, ou
    // ouvre le choix de profil -- lu AVANT tout demarrage de chapitre 2, jamais influence par
    // `chapter` (construit sur le chapitre 1 tant que l'ecran titre est a l'ecran).
    const hasChapter1Archive = loadArchivedDossier(1) !== null;
    const title = new TitleView(container, resumeSceneTitle, hasChapter1Archive, {
      onNewGame: () => {
        // Graine fraiche et TOUJOURS le chapitre 1 (retour de QA) : jamais la graine ni le
        // chapitre de la session eventuellement reprise en arriere-plan par `chapter`.
        chapter.startChapter(1);
        chapter.setMenuVisible(false);
        title.dismiss();
      },
      onResume: () => {
        chapter.setMenuVisible(false);
        title.dismiss();
      },
      onContinueChapter2: () => {
        chapter.startChapter(2, { useArchive: true });
        chapter.setMenuVisible(false);
        title.dismiss();
      },
      onChooseProfile: (profile) => {
        chapter.startChapter(2, { profile });
        chapter.setMenuVisible(false);
        title.dismiss();
      },
      // QA (src/dev/scenePresets.ts) : ecrit la partie de la branche choisie comme une
      // sauvegarde ordinaire, puis recharge sur sa graine -- `ChapterApp` la REPREND
      // (`isResumingRun`), sans point d'entree dedie. `?seed=` saute l'ecran titre.
      onJumpToScene: ({ variantId, seed, profile }) => {
        const runSeed = seed ?? randomSeedLabel();
        const start = buildScenePresetStart(variantId, { seed: runSeed, profile });
        if (!start) return;
        chapter.dispose();
        saveDossier(start.dossier);
        saveSession({ ...loadSession(), lastSeed: runSeed, run: start.run });
        const next = new URLSearchParams(window.location.search);
        next.set('seed', runSeed);
        window.location.search = next.toString();
      },
    });
  }
}
